import React, {useCallback, useState} from 'react'
import _ from 'lodash'
import {Input} from 'antd'
import {Link} from 'react-router-dom'
import useTranslate from '../../../../modules/local/useTranslate'
import useAsync from '../../../../modules/asyncCache/useAsync'
import getLinkToDetail from '../../../../helpers/getLinkToDetail'
import {useHistory} from '../../../../modules/navigation/useRouter'
import {staticPaths} from '../../../../routes/staticPaths'
import {bindQueryParam} from '../../functions/routerHelper'
import {game_getGames_Api, game_getPreviewQuestion_Api} from 'apis'
import {RiSearchLine} from 'react-icons/ri'
import {TbArrowsSort, TbRefresh} from 'react-icons/tb'
import {BiListCheck} from 'react-icons/bi'
import {BsLink45Deg, BsPlayFill, BsPuzzleFill} from 'react-icons/bs'

const ALPHA = ['A', 'B', 'C', 'D', 'E', 'F']

// label/icon phần hiển thị thuần FE - giữ local, khớp đúng answer_type kỹ thuật (xem GamesPage.js)
const GAME_TYPE_ICONS = {
    single_choice: <BiListCheck size={16} className="text-purple-600"/>,
    crossword: <BsPuzzleFill size={14} className="text-purple-600"/>,
    matching: <BsLink45Deg size={15} className="text-purple-600"/>,
    ordering: <TbArrowsSort size={16} className="text-purple-600"/>,
}
const GAME_TYPE_LABELS = {
    single_choice: 'multiple choice',
    crossword: 'crossword',
    matching: 'matching pairs',
    ordering: 'ordering',
}

// Block quảng bá Games ở trang tin tức - thay QuizBlock cũ (poll_questions). Mục tiêu: mời gọi
// + giữ chân qua 1 câu hỏi "nếm thử" thật (game_getPreviewQuestion_Api - random, không lộ đáp
// án đúng, KHÔNG tạo game_plays nên xem lướt qua feed không tính lượt chơi/không trừ tim của
// ai). Chọn đáp án = điều hướng thẳng sang trang chơi thật, nơi phiên chơi thật sự bắt đầu.

const SuggestionBanner = ({game}) => {
    const t = useTranslate()
    if (!game) return null
    return (
        <div
            className="p-5 md:p-6 rounded-2xl flex flex-col md:flex-row md:items-center md:justify-between gap-4"
            style={{background: 'linear-gradient(135deg, #6b21a8 0%, #9333ea 60%, #a855f7 100%)'}}>
            <div className="flex flex-col gap-1.5 min-w-0">
                <span
                    style={{padding: '3px 10px', width: 'fit-content'}}
                    className="inline-flex items-center gap-1 text-2xs font-bold text-purple-800 bg-white bg-opacity-85 rounded-full uppercase tracking-wide">
                    💡 {t('Suggested Today')}
                </span>
                <h3 className="font-bold text-xl text-white leading-snug">
                    {t('play and grow in catholic faith')}
                </h3>
                <p className="text-sm text-purple-100">
                    {t('try this quiz now')} <strong>{game.title}</strong> {t('and challenge yourself today')}
                </p>
            </div>
            <Link
                to={getLinkToDetail(game) + '/play'}
                style={{minHeight: 44}}
                className="flex-shrink-0 flex items-center justify-center gap-2 font-bold text-sm text-purple-700 bg-white hover:bg-purple-50 rounded-xl no-underline px-5 transition-all">
                {t('try quick sample quiz')} →
            </Link>
        </div>
    )
}

// Nội dung xem trước tuỳ answer_type - chỉ hiển thị (không bấm chọn được), vì phần "đáp án đúng"
// bị ẩn khác nhau tuỳ loại (xem GameController::getPreviewQuestion) nên không thể chấm tại đây.
const PreviewOptions = ({answerType, preview}) => {
    if (answerType === 'matching') {
        const left = preview?.options?.left ?? []
        const right = preview?.options?.right ?? []
        return (
            <div className="grid grid-cols-2 gap-2">
                <div className="flex flex-col gap-1.5">
                    {left.map((o) => (
                        <div
                            key={o.id}
                            style={{minHeight: 40}}
                            className="px-3 flex items-center border border-color-100 rounded-lg text-sm font-semibold text-color-000">
                            {o.title}
                        </div>
                    ))}
                </div>
                <div className="flex flex-col gap-1.5">
                    {right.map((o) => (
                        <div
                            key={o.id}
                            style={{minHeight: 40}}
                            className="px-3 flex items-center border border-dashed border-purple-200 rounded-lg text-sm text-color-300">
                            {o.title}
                        </div>
                    ))}
                </div>
            </div>
        )
    }

    if (answerType === 'ordering') {
        const items = preview?.options ?? []
        return (
            <div className="flex flex-col gap-2">
                {items.map((o) => (
                    <div
                        key={o.id}
                        style={{minHeight: 44}}
                        className="px-4 flex items-center gap-3 border border-color-100 rounded-xl text-sm font-semibold text-color-000">
                        <span className="w-5 h-5 flex-shrink-0 flex items-center justify-center rounded-full bg-purple-50 text-purple-700 text-2xs">↕</span>
                        <span className="flex-1">{o.title}</span>
                    </div>
                ))}
            </div>
        )
    }

    if (answerType === 'crossword') {
        const length = preview?.meta?.answer_length ?? 5
        return (
            <div className="flex items-center gap-1.5 flex-wrap">
                {Array.from({length}).map((_v, i) => (
                    <span
                        key={i}
                        style={{width: 32, height: 32}}
                        className="flex items-center justify-center border-2 border-dashed border-purple-200 rounded-lg font-bold text-purple-300">
                        ?
                    </span>
                ))}
            </div>
        )
    }

    // single_choice (mặc định)
    const options = preview?.options ?? []
    return (
        <div className="flex flex-col gap-2">
            {options.map((opt, index) => (
                <div
                    key={opt.id}
                    style={{minHeight: 44}}
                    className="px-4 flex items-center gap-3 border border-color-100 rounded-xl text-sm font-semibold text-color-000">
                    <span className="w-6 h-6 flex-shrink-0 flex items-center justify-center rounded-full bg-purple-50 text-purple-700 text-xs font-bold">
                        {ALPHA[index] ?? index + 1}
                    </span>
                    <span className="flex-1">{opt.title}</span>
                </div>
            ))}
        </div>
    )
}

export const QuizPreviewCard = ({preview, onReload, loading}) => {
    const t = useTranslate()
    const game = preview?.game
    const answerType = preview?.answer_type
    const playLink = game ? getLinkToDetail(game) + '/play' : '#'

    return (
        <div className="p-4 md:p-5 background border border-color-50 shadow-sm rounded-2xl flex flex-col gap-3">
            <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-1.5 flex-wrap">
                    <span
                        style={{padding: '2px 10px'}}
                        className="flex items-center gap-1 text-2xs font-bold text-purple-700 bg-purple-50 rounded-full uppercase tracking-wide">
                        {GAME_TYPE_ICONS[answerType]}
                        {t(GAME_TYPE_LABELS[answerType] ?? 'multiple choice')}
                    </span>
                    {!!game?.difficulty && (
                        <span
                            style={{padding: '2px 10px'}}
                            className="text-2xs font-bold text-amber-700 bg-amber-50 rounded-full uppercase tracking-wide">
                            {t(game.difficulty)}
                        </span>
                    )}
                </div>
                <button
                    type="button"
                    onClick={onReload}
                    disabled={loading}
                    className="flex-shrink-0 flex items-center gap-1 text-xs font-semibold text-color-400 hover:text-purple-600 cursor-pointer disabled:opacity-40">
                    <TbRefresh size={14} className={loading ? 'animate-spin' : ''}/>
                    {t('change question')}
                </button>
            </div>

            <span className="text-xs font-bold text-purple-600 uppercase tracking-wide">
                {t('quick challenge question')}
            </span>
            <h4 className="font-bold text-base text-color-000 leading-snug min-h-10">
                {preview?.question_text}
            </h4>

            <PreviewOptions answerType={answerType} preview={preview}/>

            <Link
                to={playLink}
                style={{minHeight: 44}}
                className="w-full flex items-center justify-center gap-2 font-bold text-sm text-white bg-purple-600 hover:bg-purple-700 rounded-xl no-underline transition-all">
                {t('try now')} →
            </Link>
        </div>
    )
}

const QuizSetItem = ({game}) => {
    const t = useTranslate()
    return (
        <Link
            to={getLinkToDetail(game)}
            className="py-2.5 flex items-center justify-between gap-3 border-b border-color-50 last:border-b-0 no-underline text-color-000">
            <div className="flex items-center gap-2.5 min-w-0 flex-1">
                <span
                    style={{width: 32, height: 32, flexShrink: 0}}
                    className="flex items-center justify-center rounded-lg bg-purple-50">
                    {GAME_TYPE_ICONS[game.primary_answer_type] ?? <BiListCheck size={16} className="text-purple-600"/>}
                </span>
                <div className="flex flex-col min-w-0 flex-1">
                    <span className="font-semibold text-sm truncate">{game.title}</span>
                    {game.total_of_questions != null && (
                        <span className="text-2xs text-color-400">
                            {game.total_of_questions + ' ' + t('question count unit')}
                        </span>
                    )}
                </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
                {!!game.difficulty && (
                    <span
                        style={{padding: '1px 8px'}}
                        className="text-2xs font-bold text-amber-700 bg-amber-50 rounded-full uppercase">
                        {t(game.difficulty)}
                    </span>
                )}
                <span
                    style={{width: 26, height: 26}}
                    className="flex flex-center rounded-full bg-purple-50 text-purple-600">
                    <BsPlayFill size={12}/>
                </span>
            </div>
        </Link>
    )
}

const QuizSetsSidebar = () => {
    const t = useTranslate()
    const history = useHistory()
    const [keyword, setKeyword] = useState('')
    const {response} = useAsync({
        apiInfo: game_getGames_Api,
        query: bindQueryParam({}),
        values: {sort_by: 'hot'},
    })
    const items = _.get(response, 'data.data') ?? []

    const submitSearch = useCallback(() => {
        const sp = new URLSearchParams()
        sp.set('tab', 'directory')
        if (keyword) sp.set('game-keyword', keyword)
        history.push({pathname: staticPaths.games.pathname, search: '?' + sp.toString()})
    }, [history, keyword])

    return (
        <div className="p-4 background border border-color-50 shadow-sm rounded-2xl flex flex-col gap-3">
            <div className="flex items-center justify-between gap-2">
                <span className="flex items-center gap-1.5 font-bold text-sm text-color-000">
                    📚 {t('quiz sets library')}
                </span>
                {items.length > 0 && (
                    <span className="text-2xs text-color-400 flex-shrink-0">
                        {items.length + ' ' + t('sets available')}
                    </span>
                )}
            </div>
            <Input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                onPressEnter={submitSearch}
                allowClear
                prefix={<RiSearchLine size={13} className="text-color-400"/>}
                placeholder={t('search game placeholder')}
                className="rounded-lg"
            />
            <div className="flex flex-col">
                {items.slice(0, 4).map((game) => <QuizSetItem key={game.id} game={game}/>)}
            </div>
            <Link
                to={staticPaths.games.pathname}
                className="flex items-center justify-center gap-1 text-sm font-semibold text-purple-600 hover:text-purple-700 no-underline pt-1">
                {t('explore all quiz sets')} ›
            </Link>
        </div>
    )
}

export const GamesPromoBlock = () => {
    const [reloadKey, setReloadKey] = useState(0)
    const {response, isLoading} = useAsync({
        apiInfo: game_getPreviewQuestion_Api,
        deps: [reloadKey],
    })
    const preview = _.get(response, 'data.data')

    const reload = useCallback(() => setReloadKey((v) => v + 1), [])

    return (
        <div className="flex flex-col gap-4">
            <SuggestionBanner game={preview?.game}/>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <QuizPreviewCard preview={preview} onReload={reload} loading={isLoading}/>
                <QuizSetsSidebar/>
            </div>
        </div>
    )
}

export default GamesPromoBlock
